import * as THREE from 'three'

import { VRM, VRMHumanBoneName, VRMUtils } from '@pixiv/three-vrm'

import {
  AVATAR_BASE_HEIGHT,
  AVATAR_PLACEHOLDER_DEPTH,
  AVATAR_PLACEHOLDER_WIDTH,
} from './avatarPoseConstants'

import { ObjectRuntimeState } from '../../types'

/** 博士キャラ VRM の配信 URL（public/assets 配下に静的配置・遅延フェッチ）。 */
export const HAKASE_VRM_URL = '/assets/hakase.vrm'

/**
 * VRM モデルの正面方向補正。ハカセ VRM（VRM 0.x 系）は既に three.js 標準
 * forward (-Z) と同じ向きで作られているので補正は不要 (= 0)。
 * VRM 1.0 で +Z 向きのモデルを使う場合はここを Math.PI にする。
 */
const VRM_FORWARD_FIX_Y = 0

/** GLTF userData から VRM ペイロードを型ガードで安全に取り出す（any/as 排除）。 */
const hasVrmField = (value: unknown): value is { readonly vrm: unknown } =>
  typeof value === 'object' && value !== null && 'vrm' in value

export const extractVrm = (userData: unknown): VRM | null => {
  if (!hasVrmField(userData)) {
    return null
  }
  return userData.vrm instanceof VRM ? userData.vrm : null
}

/**
 * 博士 VRM 3D アバター。
 * - group は state.position/rotation を反映するコンテナ。VRM モデルは modelRoot の子。
 * - 既存カメラ/ビルダーコードと互換を保つため group/scaleHKey/visible の意味は据え置く。
 *   group.rotation.y は state.rotationY を反映し、カメラ follow ヨーも同じく参照する。
 * - placeholder（簡素な箱）は VRM ロード中/失敗時のフォールバック表示。
 * - bob はその場の上下バウンス。runTime は移動位相の累積、bobOffset は実際に modelRoot へ
 *   適用する量（停止時は指数 lerp で 0 へ復帰させる）。
 * - modelMeasuredHeight は VRM の素のバウンディング高さ（メートル単位）で、scaleH 反映時に
 *   AVATAR_BASE_HEIGHT * scaleH / modelMeasuredHeight を modelRoot.scale に乗せる。
 */
export interface VrmAvatarEntry {
  readonly group: THREE.Group
  /** VRM の scene を内包するサブグループ（bob や 180° 補正をここに乗せる）。 */
  readonly modelRoot: THREE.Group
  /** placeholder 用の箱（VRM 読込前/失敗時の表示）。成功時は scene graph から外す。 */
  readonly placeholderMesh: THREE.Mesh
  readonly placeholderGeometry: THREE.BoxGeometry
  readonly placeholderMaterial: THREE.MeshStandardMaterial
  /** 読み込んだ VRM（成功時のみセット）。dispose で deepDispose する。 */
  vrm: VRM | null
  /** 読み込んだ VRM のバウンディング高さ（scaleH 反映時のスケール計算に使う）。 */
  modelMeasuredHeight: number | null
  /** モデル空間での bbox 最低 Y（足裏を group 中心の下端に合わせる際に使う）。 */
  modelMinY: number | null
  /** 現在の scaleH に基づく modelRoot の静止 Y（bobOffset 加算前の基準位置）。 */
  modelRestY: number
  /** dispose / removeAvatar 後の遅延コールバックを破棄するための世代。 */
  loadToken: number
  /** 直近の state.size.y / AVATAR_BASE_HEIGHT。スケール更新の差分検出に使用。 */
  scaleHKey: number | null
  /** 前フレームの XZ 位置。移動速度の差分計算用。 */
  prevX: number
  prevZ: number
  /** 前フレームの Y 位置。垂直速度の差分計算用（ジャンプ判定）。 */
  prevY: number
  /** 接地基準 Y。最初に静止判定できた y を記録し、その後の閾値超えで airborne とする。 */
  groundedY: number | null
  /** 直近で airborne 判定だったか。立ち上がり/着地遷移検出に使う。 */
  airborne: boolean
  /**
   * 走行中の下り坂・段差で grounded がチラついた際、走行モーションが空中ポーズへ
   * 飛ぶのを抑える残り猶予（秒）。接地でリチャージし、非接地中に減算する。
   */
  airborneIgnoreRemaining: number
  /**
   * 直近に観測した motionState.respawnGeneration。値が変わったフレームを
   * 「テレポート復帰（リスポーン/リセット）」とみなし、誤った着地屈伸を抑止する。
   */
  lastRespawnGeneration: number
  /** 空中姿勢のブレンド量（0..1）。airborne 中は 1 へ、接地で 0 へ指数 lerp。 */
  jumpPoseAmp: number
  /** 着地直後の屈伸残時間（秒）。0 で通常姿勢に復帰。 */
  landingCrouchRemaining: number
  /** テイクオフ衝撃の残時間（秒）。離陸の瞬間に 'TAKEOFF_IMPULSE_DURATION' から減衰。 */
  takeoffImpulseRemaining: number
  /**
   * 死亡演出（体力 0）の倒れポーズのブレンド量（0..1）。死亡中は 1 へ指数 lerp、
   * リスポーンで 0 へ即時リセットする。
   */
  deathPoseAmp: number
  /** 倒れ込みの回転符号（-1=前傾 / +1=後傾）。物理側の deathFall.direction 由来。 */
  deathFallSign: number
  /** 倒れ込みの最大角（ラジアン）。障害物に届かない角度で止めて貫通を防ぐ。 */
  deathFallMaxAngleRad: number
  /** 直近に観測した deathKind。'void' の表示切替（非表示/復帰）の遷移検出に使う。 */
  lastDeathKind: 'health' | 'void' | null
  /**
   * 距離駆動の位相累積（ラジアン）。'累積水平距離 / cycleDistance * 2π' で増える。
   * 停止中は据え置きにして再開時に sin の連続性を保つ。
   */
  runTime: number
  /** 現在 modelRoot.position.y へ適用しているバウンス量（停止時は 0 へ lerp）。 */
  bobOffset: number
  /** 走りエンベロープ（0..1）。移動中は 1 へ、停止時は 0 へ指数 lerp する。 */
  strideAmplitude: number
  /** ダッシュエンベロープ（0..1）。ダッシュ中は 1 へ、解除で 0 へ指数 lerp する。 */
  dashAmplitude: number
  /** 遊泳ポーズのブレンド量（0..1）。遊泳中は 1 へ、陸上で 0 へ指数 lerp する。 */
  swimPoseAmp: number
  /** 平泳ぎ（移動入力あり）⇔ 浮遊（入力なし）のブレンド量（0..1）。 */
  swimMoveAmp: number
  /** すべり台滑走ポーズのブレンド量（0..1）。滑走中は 1 へ、降りたら 0 へ。 */
  slidePoseAmp: number
  /** 着席（vehicle 乗車）ポーズのブレンド量（0..1）。乗車中は 1 へ、降車で 0 へ。 */
  sitPoseAmp: number
  /** 直近フレームの遊泳移動入力。立ち上がり検出（サイクル先頭から開始）に使う。 */
  wasSwimmingMoving: boolean
  /** 遊泳ストロークの経過時間（秒、サイクル長で循環）。 */
  swimTime: number
  /**
   * humanoid 骨参照（VRM ロード成功時に解決）。走りアニメで rotation.x を駆動するため
   * キャッシュしておく。null は骨が無い VRM や未ロードを表す安全な縮退ケース。
   */
  leftUpperArm: THREE.Object3D | null
  rightUpperArm: THREE.Object3D | null
  leftUpperLeg: THREE.Object3D | null
  rightUpperLeg: THREE.Object3D | null
  /** 下脚（膝下）。屈伸/空中で膝を曲げるための回転対象。 */
  leftLowerLeg: THREE.Object3D | null
  rightLowerLeg: THREE.Object3D | null
  /** 足（足首）。走りで「かかと着地→つま先で蹴る」足首ロールを表現する回転対象。 */
  leftFoot: THREE.Object3D | null
  rightFoot: THREE.Object3D | null
  /** 下腕（肘より先）。屈伸時の肘曲げや空中での腕の振りに用いる。 */
  leftLowerArm: THREE.Object3D | null
  rightLowerArm: THREE.Object3D | null
  /** 頭・首。遊泳の呼吸（首を曲げて前を向く）に用いる。 */
  head: THREE.Object3D | null
  neck: THREE.Object3D | null
  /**
   * 指関節と拳ポーズの回転量（VRM ロード時に解決）。通常は握り拳で、
   * 遊泳中は swimAmp に応じて開く（指を全て伸ばして水を掻く）。
   */
  fingerJoints: { bone: THREE.Object3D; restY: number; restZ: number }[]
}

/**
 * A ポーズ補正の上腕回転量（ラジアン、約 80°）。
 *
 * VRM 1.0 humanoid 正規化骨で、T ポーズ(水平)からこの角だけ Z 軸まわりに
 * 倒すと、腕がほぼ垂直近くまで落ちて脇がしっかり締まる。値を 90°(=π/2 ≒ 1.57)
 * に近づけるほど直立に近づき、腕が体側に張り付く印象になる。
 */
export const REST_POSE_UPPER_ARM_Z = 1.4

/**
 * 拳を握るときの各指関節の曲げ角（ラジアン）。VRM 1.0 humanoid 仕様の
 * 指 bone を一括で curl して握り拳ポーズを作る（静的、起動時に 1 度だけ適用）。
 * 値を強くしすぎると指が重なって不自然な塊になるので、Proximal は浅め、
 * Intermediate を深め、Distal はやや浅めにして自然な握り拳を作る。
 */
const FIST_PROX_BEND = 1.1 // 約 63°（手首付近の付け根）
const FIST_INTER_BEND = 1.2 // 約 69°（中間関節）
const FIST_DISTAL_BEND = 0.6 // 約 34°（指先）
/**
 * 親指の握り込み角（ラジアン）。親指は他の 4 指と屈曲軸が異なり（対向性）、
 * Z 軸 flexion だけだと立てた親指（サムズアップ）に見えてしまう。
 * Y 軸で手のひら側へ握り込み、Z で指列に沿わせて拳の中へ収める。
 */
export const FIST_THUMB_CURL_Y = 1.0 // 約 57°（手のひら側への握り込み）
export const FIST_THUMB_BEND_Z = 0.35 // 約 20°（指列に沿わせる）

/** 拳ポーズ対象の指関節（左右ペア × 関節別の曲げ角）。 */
export const FIST_BONE_PAIRS: ReadonlyArray<{
  readonly left: VRMHumanBoneName
  readonly right: VRMHumanBoneName
  readonly bend: number
}> = [
  // 指 4 本 × Proximal / Intermediate / Distal の 3 関節
  {
    left: 'leftIndexProximal',
    right: 'rightIndexProximal',
    bend: FIST_PROX_BEND,
  },
  {
    left: 'leftIndexIntermediate',
    right: 'rightIndexIntermediate',
    bend: FIST_INTER_BEND,
  },
  {
    left: 'leftIndexDistal',
    right: 'rightIndexDistal',
    bend: FIST_DISTAL_BEND,
  },
  {
    left: 'leftMiddleProximal',
    right: 'rightMiddleProximal',
    bend: FIST_PROX_BEND,
  },
  {
    left: 'leftMiddleIntermediate',
    right: 'rightMiddleIntermediate',
    bend: FIST_INTER_BEND,
  },
  {
    left: 'leftMiddleDistal',
    right: 'rightMiddleDistal',
    bend: FIST_DISTAL_BEND,
  },
  {
    left: 'leftRingProximal',
    right: 'rightRingProximal',
    bend: FIST_PROX_BEND,
  },
  {
    left: 'leftRingIntermediate',
    right: 'rightRingIntermediate',
    bend: FIST_INTER_BEND,
  },
  { left: 'leftRingDistal', right: 'rightRingDistal', bend: FIST_DISTAL_BEND },
  {
    left: 'leftLittleProximal',
    right: 'rightLittleProximal',
    bend: FIST_PROX_BEND,
  },
  {
    left: 'leftLittleIntermediate',
    right: 'rightLittleIntermediate',
    bend: FIST_INTER_BEND,
  },
  {
    left: 'leftLittleDistal',
    right: 'rightLittleDistal',
    bend: FIST_DISTAL_BEND,
  },
]

/**
 * 親指の関節（Metacarpal 含む 3 関節）。拳の中へ握り込むため、
 * 他の 4 指（Z flexion のみ）と別系統で Y+Z の複合回転を適用する。
 */
export const FIST_THUMB_PAIRS: ReadonlyArray<{
  readonly left: VRMHumanBoneName
  readonly right: VRMHumanBoneName
}> = [
  { left: 'leftThumbMetacarpal', right: 'rightThumbMetacarpal' },
  { left: 'leftThumbProximal', right: 'rightThumbProximal' },
  { left: 'leftThumbDistal', right: 'rightThumbDistal' },
]

/**
 * T ポーズの腕を A ポーズ（やや下げた自然姿勢）に補正し、両手を握り拳にする。
 * VRM 1.0 の正規化 humanoid 骨を取得して、上腕・各指を一括で回転する。
 * 骨が見つからない場合は何もしない（個体差に対する安全な縮退）。
 */
export const applyRestPoseAdjustments = (vrm: VRM): void => {
  const humanoid = vrm.humanoid
  if (!humanoid) {
    return
  }
  const leftUpperArm = humanoid.getNormalizedBoneNode('leftUpperArm')
  const rightUpperArm = humanoid.getNormalizedBoneNode('rightUpperArm')
  // VRM 1.0 正規化骨の Z 軸は、左上腕では正方向が下げる向き・右上腕では負方向が
  // 下げる向きに対応する（ローカル軸の左右対称）。誤って逆符号にすると T → 万歳に
  // なってしまうので符号は左右で逆に取る。
  if (leftUpperArm) {
    leftUpperArm.rotation.z = REST_POSE_UPPER_ARM_Z
  }
  if (rightUpperArm) {
    rightUpperArm.rotation.z = -REST_POSE_UPPER_ARM_Z
  }

  // 拳ポーズ: 指の curl は VRM 標準で Z 軸 flexion が一般的。Y で曲げた前回は
  // 指が手のひら方向ではなく横方向に折れて塊状に見えたので、Z に切替える。
  // 左右で curl 方向が反転する場合があるため符号を分ける（左 = 負、右 = 正）。
  for (const pair of FIST_BONE_PAIRS) {
    const leftBone = humanoid.getNormalizedBoneNode(pair.left)
    if (leftBone) {
      leftBone.rotation.x = 0
      leftBone.rotation.y = 0
      leftBone.rotation.z = pair.bend
    }
    const rightBone = humanoid.getNormalizedBoneNode(pair.right)
    if (rightBone) {
      rightBone.rotation.x = 0
      rightBone.rotation.y = 0
      rightBone.rotation.z = -pair.bend
    }
  }

  // 親指: 立てたままにせず（サムズアップ防止）、手のひら側へ握り込んで拳の中へ収める。
  // Y の符号は「左 = 正 / 右 = 負」が手のひら側（内向き）。逆にすると甲側へ
  // 反り返って見える（実機確認済み）。
  for (const pair of FIST_THUMB_PAIRS) {
    const leftBone = humanoid.getNormalizedBoneNode(pair.left)
    if (leftBone) {
      leftBone.rotation.x = 0
      leftBone.rotation.y = FIST_THUMB_CURL_Y
      leftBone.rotation.z = FIST_THUMB_BEND_Z
    }
    const rightBone = humanoid.getNormalizedBoneNode(pair.right)
    if (rightBone) {
      rightBone.rotation.x = 0
      rightBone.rotation.y = -FIST_THUMB_CURL_Y
      rightBone.rotation.z = -FIST_THUMB_BEND_Z
    }
  }
}

/** scaleH に応じて VRM スケールと placeholder の寸法を反映する。 */
export const layoutVrmAvatar = (
  entry: VrmAvatarEntry,
  scaleH: number,
): void => {
  entry.placeholderMesh.scale.set(
    AVATAR_PLACEHOLDER_WIDTH * scaleH,
    AVATAR_BASE_HEIGHT * scaleH,
    AVATAR_PLACEHOLDER_DEPTH * scaleH,
  )
  if (entry.vrm && entry.modelMeasuredHeight && entry.modelMinY !== null) {
    // 目標視覚高さ = state.size.y（= AVATAR_BASE_HEIGHT * scaleH）。
    const target = AVATAR_BASE_HEIGHT * scaleH
    const s = target / entry.modelMeasuredHeight
    entry.modelRoot.scale.set(s, s, s)
    // group 中心は collider 中心。VRM は原点近傍が足元になりがちなので、
    // bbox 最低 Y を target/2 だけ下にずらして「足裏を group 下端」に合わせる。
    entry.modelRestY = -target / 2 - entry.modelMinY * s
  }
}

/** 博士 VRM アバターを構築する。VRM 本体は非同期に modelRoot へ追加される。 */
export const createVrmAvatarEntry = (): VrmAvatarEntry => {
  const group = new THREE.Group()

  const placeholderGeometry = new THREE.BoxGeometry(1, 1, 1)
  const placeholderMaterial = new THREE.MeshStandardMaterial({
    color: 0xc8c8c8,
    roughness: 0.8,
    metalness: 0,
  })
  const placeholderMesh = new THREE.Mesh(
    placeholderGeometry,
    placeholderMaterial,
  )
  // VRM 読み込み中は灰色の箱を出さない（ロード完了で除去 / 失敗時のみフォールバック表示）。
  placeholderMesh.visible = false

  const modelRoot = new THREE.Group()
  // VRM 1.0 は +Z 向きが既定。本レンダラの forward は yaw=0 で -Z なので 180° 補正する。
  modelRoot.rotation.y = VRM_FORWARD_FIX_Y
  modelRoot.visible = false

  group.add(placeholderMesh)
  group.add(modelRoot)

  return {
    group,
    modelRoot,
    placeholderMesh,
    placeholderGeometry,
    placeholderMaterial,
    vrm: null,
    modelMeasuredHeight: null,
    modelMinY: null,
    modelRestY: 0,
    loadToken: 0,
    scaleHKey: null,
    prevX: 0,
    prevZ: 0,
    prevY: 0,
    groundedY: null,
    airborne: false,
    airborneIgnoreRemaining: 0,
    lastRespawnGeneration: 0,
    jumpPoseAmp: 0,
    landingCrouchRemaining: 0,
    takeoffImpulseRemaining: 0,
    deathPoseAmp: 0,
    deathFallSign: -1,
    deathFallMaxAngleRad: Math.PI / 2,
    lastDeathKind: null,
    runTime: 0,
    bobOffset: 0,
    strideAmplitude: 0,
    dashAmplitude: 0,
    swimPoseAmp: 0,
    swimMoveAmp: 0,
    slidePoseAmp: 0,
    sitPoseAmp: 0,
    wasSwimmingMoving: false,
    swimTime: 0,
    leftUpperArm: null,
    rightUpperArm: null,
    leftUpperLeg: null,
    rightUpperLeg: null,
    leftLowerLeg: null,
    rightLowerLeg: null,
    leftFoot: null,
    rightFoot: null,
    leftLowerArm: null,
    rightLowerArm: null,
    head: null,
    neck: null,
    fingerJoints: [],
  }
}

/** アバターの VRM・material・geometry を解放する。 */
export const disposeVrmAvatarEntry = (entry: VrmAvatarEntry): void => {
  // 進行中のロードを無効化（成功時の差し替えコールバックを破棄させる）。
  entry.loadToken += 1
  entry.placeholderGeometry.dispose()
  entry.placeholderMaterial.dispose()
  // 破棄済み VRM の bone 参照を残さない。
  entry.fingerJoints = []
  if (entry.vrm) {
    VRMUtils.deepDispose(entry.vrm.scene)
    entry.vrm = null
  }
}

/**
 * アバターに state（位置・寸法・可視）を反映する。
 * - state.rotationY は group.rotation.y へ反映（モデル本体の向き＋カメラ follow ヨーで参照）。
 * - state.color / transparency / material は無視（VRM の見た目を上書きしない）。
 */
export const applyVrmAvatarState = (
  entry: VrmAvatarEntry,
  state: ObjectRuntimeState,
): void => {
  entry.group.position.set(state.position.x, state.position.y, state.position.z)
  entry.group.rotation.set(0, state.rotationY, 0)
  entry.group.visible = state.visible

  const scaleH = Math.max(0.01, state.size.y / AVATAR_BASE_HEIGHT)
  if (entry.scaleHKey !== scaleH) {
    entry.scaleHKey = scaleH
    layoutVrmAvatar(entry, scaleH)
  }
}
